var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as _ from 'lodash';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Provider as RendererProvider, ThemeProvider } from 'react-fela';
import { felaRenderer as felaLtrRenderer, mergeThemes } from '../../lib';
import ProviderConsumer from './ProviderConsumer';
import { mergeSiteVariables } from '../../lib/mergeThemes';
/**
 * The Provider passes the CSS in JS renderer and theme to your components.
 */
var Provider = /** @class */ (function (_super) {
    __extends(Provider, _super);
    function Provider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.staticStylesRendered = false;
        _this.renderStaticStyles = function (mergedTheme) {
            // RTL WARNING
            // This function sets static styles which are global and renderer agnostic
            // With current implementation, static styles cannot differ between LTR and RTL
            // @see http://fela.js.org/docs/advanced/StaticStyle.html for details
            var siteVariables = mergedTheme.siteVariables;
            var staticStyles = _this.props.theme.staticStyles;
            if (!staticStyles)
                return;
            var renderObject = function (object) {
                _.forEach(object, function (style, selector) {
                    felaLtrRenderer.renderStatic(style, selector);
                });
            };
            staticStyles.forEach(function (staticStyle) {
                if (typeof staticStyle === 'string') {
                    felaLtrRenderer.renderStatic(staticStyle);
                }
                else if (_.isPlainObject(staticStyle)) {
                    renderObject(staticStyle);
                }
                else if (_.isFunction(staticStyle)) {
                    var preparedSiteVariables = mergeSiteVariables(siteVariables);
                    renderObject(staticStyle(preparedSiteVariables));
                }
                else {
                    throw new Error("staticStyles array must contain CSS strings, style objects, or style functions, got: " + typeof staticStyle);
                }
            });
        };
        _this.renderFontFaces = function () {
            // RTL WARNING
            // This function sets static styles which are global and renderer agnostic
            // With current implementation, static styles cannot differ between LTR and RTL
            // @see http://fela.js.org/docs/advanced/StaticStyle.html for details
            var fontFaces = _this.props.theme.fontFaces;
            if (!fontFaces)
                return;
            var renderFontObject = function (font) {
                if (!_.isPlainObject(font)) {
                    throw new Error("fontFaces must be objects, got: " + typeof font);
                }
                felaLtrRenderer.renderFont(font.name, font.paths, font.style);
            };
            fontFaces.forEach(function (font) {
                renderFontObject(font);
            });
        };
        _this.renderStaticStylesOnce = function (mergedTheme) {
            var staticStyles = _this.props.theme.staticStyles;
            if (!_this.staticStylesRendered && staticStyles) {
                _this.renderStaticStyles(mergedTheme);
                _this.staticStylesRendered = true;
            }
        };
        return _this;
    }
    Provider.prototype.componentDidMount = function () {
        this.renderFontFaces();
    };
    Provider.prototype.render = function () {
        var _this = this;
        var _a = this.props, theme = _a.theme, children = _a.children;
        // rehydration disabled to avoid leaking styles between renderers
        // https://github.com/rofrischmann/fela/blob/master/docs/api/fela-dom/rehydrate.md
        return (React.createElement(ProviderConsumer, { render: function (incomingTheme) {
                var outgoingTheme = mergeThemes(incomingTheme, theme);
                _this.renderStaticStylesOnce(outgoingTheme);
                return (React.createElement(RendererProvider, __assign({ renderer: outgoingTheme.renderer }, { rehydrate: false }),
                    React.createElement(ThemeProvider, { theme: outgoingTheme }, children)));
            } }));
    };
    Provider.propTypes = {
        theme: PropTypes.shape({
            siteVariables: PropTypes.object,
            componentVariables: PropTypes.object,
            componentStyles: PropTypes.object,
            rtl: PropTypes.bool,
            fontFaces: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string,
                paths: PropTypes.arrayOf(PropTypes.string),
                style: PropTypes.shape({
                    fontStretch: PropTypes.string,
                    fontStyle: PropTypes.string,
                    fontVariant: PropTypes.string,
                    fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    localAlias: PropTypes.string,
                    unicodeRange: PropTypes.string,
                }),
            })),
            staticStyles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func])),
            animations: PropTypes.object,
        }),
        children: PropTypes.element.isRequired,
    };
    Provider.Consumer = ProviderConsumer;
    return Provider;
}(React.Component));
export default Provider;
